// ── Caché de imágenes (LRU en memoria + Cache Storage) ───────────────────────
// Las imágenes privadas se sirven con signed URLs cuyo `token` CAMBIA en cada
// generación. Si cacheáramos por la URL completa nunca matchearía → se
// re-descargaría al volver a una pantalla. Acá:
//   1. Cacheamos por una LLAVE ESTABLE (la URL sin el `token`).
//   2. Guardamos el blob decodificado en un LRU acotado que SOBREVIVE al
//      desmontar → volver a una pantalla es instantáneo (sin re-decodificar).
//   3. Persistimos en Cache Storage (aguanta recargas; el browser lo evicta solo
//      si falta cuota).
// El LRU NUNCA evicta una imagen montada (visible). `release` no revoca: deja la
// imagen "caliente" hasta que la eviction la saque por vieja.

import { esImagenDePinterest } from '../lib/pinterestConexionLogica';

interface Entry {
  url: string; // blob object URL (o el src crudo si falló el blob)
  isObject: boolean; // si `url` es un objectURL nuestro (hay que revocarlo)
  refs: number; // cuántos <CachedImg> montados la usan ahora
}

// Map en orden de inserción = orden LRU (el primero es el más viejo).
const mem = new Map<string, Entry>();

// Tope de imágenes "calientes" en memoria. Más chico en devices de poca RAM.
const MAX_ENTRIES = (() => {
  try {
    const dm = (navigator as any).deviceMemory;
    if (typeof dm === 'number' && dm > 0 && dm <= 2) return 40;
  } catch {}
  return 80;
})();

// Promesas en vuelo: evita fetchear/decodificar 2 veces la misma imagen si dos
// componentes la piden a la vez.
const inflight = new Map<string, Promise<{ url: string; isObject: boolean }>>();

const CACHE_NAME = 'atlas-image-cache-v1';

// Llave estable: saca el `token` volátil de la signed URL de Supabase. No se usan
// transforms, así que `token` es el único parámetro que cambia entre llamadas.
export function cacheKeyFor(src: string): string {
  try {
    const base = typeof location !== 'undefined' ? location.href : 'https://x/';
    const u = new URL(src, base);
    u.searchParams.delete('token');
    const q = u.searchParams.toString();
    return u.origin + u.pathname + (q ? `?${q}` : '');
  } catch {
    return src;
  }
}

function touch(key: string, entry: Entry) {
  // Move-to-end = marcar como más reciente (queda último en el orden del Map).
  mem.delete(key);
  mem.set(key, entry);
}

function evictIfNeeded() {
  if (mem.size <= MAX_ENTRIES) return;
  for (const [key, entry] of mem) {
    if (mem.size <= MAX_ENTRIES) break;
    if (entry.refs > 0) continue; // nunca evictar una imagen visible
    if (entry.isObject) {
      try {
        URL.revokeObjectURL(entry.url);
      } catch {}
    }
    mem.delete(key);
  }
}

async function fetchBlobFor(key: string, fetchUrl: string): Promise<Blob | null> {
  // 1) Cache Storage por llave estable (persiste entre recargas).
  try {
    const cs: CacheStorage | undefined = (globalThis as any).caches;
    if (cs) {
      const cache = await cs.open(CACHE_NAME);
      const keyReq = new Request(`https://atlas-img.local/${encodeURIComponent(key)}`);
      const match = await cache.match(keyReq);
      if (match && match.ok) return await match.blob();
      const res = await fetch(fetchUrl, { mode: 'cors' });
      if (!res.ok) return null;
      try {
        await cache.put(keyReq, res.clone());
      } catch {}
      return await res.blob();
    }
  } catch {}
  // 2) Sin Cache Storage → fetch directo.
  try {
    const res = await fetch(fetchUrl, { mode: 'cors' });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

async function acquire(
  key: string,
  fetchUrl: string,
): Promise<{ url: string; isObject: boolean }> {
  // Una imagen de Pinterest NO se guarda (ni en Cache Storage ni como blob): se muestra
  // directo de su link. Pinterest prohíbe guardar su contenido
  // (ADR-2026-10-29-pinterest-en-recrear-con-plantillas).
  if (esImagenDePinterest(fetchUrl)) return { url: fetchUrl, isObject: false };
  const existing = mem.get(key);
  if (existing) {
    existing.refs += 1;
    touch(key, existing);
    return { url: existing.url, isObject: existing.isObject };
  }
  const pending = inflight.get(key);
  if (pending) {
    const r = await pending;
    const e = mem.get(key);
    if (e) e.refs += 1;
    return r;
  }
  const p = (async () => {
    const blob = await fetchBlobFor(key, fetchUrl);
    if (!blob) {
      // Fallback: usar el src crudo (el browser lo carga directo).
      const entry: Entry = { url: fetchUrl, isObject: false, refs: 1 };
      mem.set(key, entry);
      return { url: fetchUrl, isObject: false };
    }
    const url = URL.createObjectURL(blob);
    const entry: Entry = { url, isObject: true, refs: 1 };
    mem.set(key, entry);
    evictIfNeeded();
    return { url, isObject: true };
  })();
  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
}

function release(key: string) {
  const entry = mem.get(key);
  if (entry && entry.refs > 0) entry.refs -= 1;
  // No revoca: el LRU mantiene la imagen "caliente" hasta evictarla por vieja.
}

// Lookup SÍNCRONO (sin fetch) — para que CachedImg pinte al instante si ya está.
export function peekCachedImageUrl(src: string): string | null {
  if (!src) return null;
  const entry = mem.get(cacheKeyFor(src));
  return entry ? entry.url : null;
}

export async function getCachedImageUrl(
  src: string,
): Promise<{ url: string; isObject: boolean }> {
  if (!src) return { url: '', isObject: false };
  return acquire(cacheKeyFor(src), src);
}

export function releaseCachedImageUrl(src: string) {
  if (!src) return;
  release(cacheKeyFor(src));
}

// Cache por una llave explícita (ej. bucket path) mientras se fetchea de una URL
// firmada/volátil. Hoy `getCachedImageUrl` ya deriva la llave solo, pero se
// mantiene para los callers existentes que pasan el path a mano.
export async function getCachedImageByKey(
  key: string,
  fetchUrl: string,
): Promise<{ url: string; isObject: boolean }> {
  return acquire(key, fetchUrl);
}

export function releaseCachedByKey(key: string) {
  release(key);
}

// ── Signed URL cache ──────────────────────────────────────────────────
// Cachea bucket-path → signedUrl para saltear `createSignedUrl` en cada remount.
// Las entradas expiran a los `ttlMs` (default 50 min; las signed URLs duran 60).

interface SignedUrlEntry {
  signedUrl: string;
  expiresAt: number;
}

const signedUrlMap = new Map<string, SignedUrlEntry>();
const SIGNED_URL_TTL_MS = 50 * 60 * 1000; // 50 minutos

export function getCachedSignedUrl(bucketPath: string): string | null {
  const entry = signedUrlMap.get(bucketPath);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    signedUrlMap.delete(bucketPath);
    return null;
  }
  return entry.signedUrl;
}

export function setCachedSignedUrl(
  bucketPath: string,
  signedUrl: string,
  ttlMs = SIGNED_URL_TTL_MS,
) {
  signedUrlMap.set(bucketPath, { signedUrl, expiresAt: Date.now() + ttlMs });
}

export function clearSignedUrlCache() {
  signedUrlMap.clear();
}
